import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import Button from './Button';
import MatchChatBox from './MatchChatBox';
import TeamHoverName from './TeamHoverName';
import { getRoundLabel } from '../utils/bracketUtils';

const STATUS_STYLES = {
  scheduled:            'bg-slate-50 text-slate-500 border-slate-200',
  pending_confirmation: 'bg-amber-50 text-amber-700 border-amber-200',
  completed:            'bg-emerald-50 text-emerald-700 border-emerald-200',
  bye:                  'bg-slate-50 text-slate-400 border-slate-200',
};

const STATUS_LABELS = {
  scheduled:            'Offen',
  pending_confirmation: 'Bestätigung ausstehend',
  completed:            'Abgeschlossen',
  bye:                  'BYE',
};

export default function MatchModal({ match, user, isAdmin = false, finalRound = 0, onClose, onRefresh }) {
  const [score1, setScore1] = useState(match?.score_team1 ?? '');
  const [score2, setScore2] = useState(match?.score_team2 ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [tab, setTab] = useState('result');

  if (!match) return null;

  const isCreatorOfTeam1 = user && match.team1 && match.team1.creator_id === user.id;
  const isCreatorOfTeam2 = user && match.team2 && match.team2.creator_id === user.id;
  const isAnyCreator = isCreatorOfTeam1 || isCreatorOfTeam2;

  const isScheduled = match.status === 'scheduled' || (match.status === 'bye' && match.team1 && match.team2);
  const isPending   = match.status === 'pending_confirmation';
  const isCompleted = match.status === 'completed';
  const isBye       = match.status === 'bye' && !(match.team1 && match.team2);

  const canSubmit  = !isBye && match.team1 && match.team2 && (isAdmin || (isScheduled && isAnyCreator));
  const canConfirm = isPending && (isAdmin || isAnyCreator);
  const canWrite   = isAdmin || isAnyCreator || !!user;

  const submitResult = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const url = isAdmin
        ? `/api/admin/matches/${match.id}/result`
        : `/api/matches/${match.id}/result`;
      const method = isAdmin ? api.put : api.post;
      await method(url, { score_team1: parseInt(score1), score_team2: parseInt(score2) });
      setMessage(isAdmin ? 'Ergebnis gesetzt.' : 'Ergebnis eingereicht – warte auf Bestätigung.');
      onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSubmitting(false); }
  };

  const confirm = async () => {
    setConfirming(true); setError('');
    try {
      await api.post(`/api/matches/${match.id}/confirm`);
      setMessage('Ergebnis bestätigt!');
      onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setConfirming(false); }
  };

  const reject = async () => {
    setRejecting(true); setError('');
    try {
      await api.post(`/api/matches/${match.id}/reject`);
      setMessage('Ergebnis abgelehnt und zurückgesetzt.');
      onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setRejecting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              {getRoundLabel(match.round, match.bracket_side, finalRound)}
            </div>
            <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded border ${STATUS_STYLES[match.status] || ''}`}>
              {STATUS_LABELS[match.status] || match.status}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none ml-4 mt-1">&times;</button>
        </div>

        {/* Teams */}
        <div className="flex items-center gap-3 px-5 py-4">
          <div className={`flex-1 rounded-xl border-2 p-3 text-center ${isCompleted && match.winner_id === match.team1_id ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}>
            <div className="font-bold text-slate-800 truncate text-sm">
              {match.team1 ? <TeamHoverName team={match.team1} /> : 'TBD'}
            </div>
            {isCompleted && <div className="text-2xl font-black text-slate-700 mt-1">{match.score_team1 ?? '–'}</div>}
            {isCompleted && match.winner_id === match.team1_id && <div className="text-xs text-emerald-600 font-bold mt-1">Gewinner</div>}
          </div>
          <div className="text-slate-300 font-bold text-lg flex-shrink-0">vs</div>
          <div className={`flex-1 rounded-xl border-2 p-3 text-center ${isCompleted && match.winner_id === match.team2_id ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}>
            <div className="font-bold text-slate-800 truncate text-sm">
              {match.team2 ? <TeamHoverName team={match.team2} /> : 'TBD'}
            </div>
            {isCompleted && <div className="text-2xl font-black text-slate-700 mt-1">{match.score_team2 ?? '–'}</div>}
            {isCompleted && match.winner_id === match.team2_id && <div className="text-xs text-emerald-600 font-bold mt-1">Gewinner</div>}
          </div>
        </div>

        {isCompleted && match.result_by_admin && (
          <div className="mx-5 mb-2 flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
            <span className="font-bold bg-red-500 text-white px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wide">Admin</span>
            <span>Ergebnis wurde von einem Admin eingetragen</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-5">
          {['result', 'chat'].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t ? 'border-ocean-500 text-ocean-700' : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              {t === 'result' ? 'Ergebnis' : 'Chat'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="overflow-y-auto flex-1 p-5">
          {tab === 'result' && (
            <>
              {message && <div className="alert-success mb-3 text-sm">{message}</div>}
              {error   && <div className="alert-error mb-3 text-sm">{error}</div>}

              {canSubmit && !message && (
                <form onSubmit={submitResult}>
                  <p className="text-sm font-semibold text-slate-700 mb-3">
                    {isAdmin ? 'Ergebnis direkt setzen' : 'Ergebnis eintragen'}
                  </p>
                  <div className="flex items-end gap-3">
                    <div className="flex-1 text-center">
                      <div className="text-xs text-slate-500 mb-1 truncate">{match.team1?.name}</div>
                      <input
                        type="number" min="0" max="99" value={score1}
                        onChange={(e) => setScore1(e.target.value)}
                        className="w-full text-center text-2xl font-bold border-2 border-slate-200 rounded-xl p-2 focus:border-ocean-400 focus:outline-none"
                        placeholder="0" required
                      />
                    </div>
                    <div className="text-slate-300 font-bold pb-2">:</div>
                    <div className="flex-1 text-center">
                      <div className="text-xs text-slate-500 mb-1 truncate">{match.team2?.name}</div>
                      <input
                        type="number" min="0" max="99" value={score2}
                        onChange={(e) => setScore2(e.target.value)}
                        className="w-full text-center text-2xl font-bold border-2 border-slate-200 rounded-xl p-2 focus:border-ocean-400 focus:outline-none"
                        placeholder="0" required
                      />
                    </div>
                  </div>
                  <Button type="submit" loading={submitting} className="w-full mt-3">
                    {isAdmin ? 'Ergebnis setzen' : 'Ergebnis einreichen'}
                  </Button>
                </form>
              )}

              {canConfirm && !message && (
                <div>
                  <p className="text-sm text-amber-700 font-semibold mb-3">
                    Eingereichtes Ergebnis: {match.score_team1} – {match.score_team2}
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={confirm} loading={confirming} className="flex-1">Bestätigen</Button>
                    <Button onClick={reject} loading={rejecting} variant="secondary" className="flex-1">Ablehnen</Button>
                  </div>
                </div>
              )}

              {isBye && <p className="text-sm text-slate-400 text-center py-4">BYE – kein Gegner</p>}

              {isCompleted && !canSubmit && !message && (
                <p className="text-sm text-slate-400 text-center py-2">Match abgeschlossen.</p>
              )}

              {!canSubmit && !canConfirm && !isBye && !isCompleted && !message && (
                <p className="text-sm text-slate-400 text-center py-2">Kein Zugriff auf Ergebnis-Eintragung.</p>
              )}
            </>
          )}

          {tab === 'chat' && (
            <MatchChatBox matchId={match.id} canWrite={canWrite} />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-5 py-3">
          <Link to={`/matches/${match.id}`} className="text-xs text-ocean-600 hover:underline">
            Vollständige Match-Details öffnen →
          </Link>
        </div>
      </div>
    </div>
  );
}
